import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, FileText, Fingerprint, MapPin, AlertOctagon } from 'lucide-react';
import axios from 'axios';
import { useApp } from '@/contexts/AppContext';

const ICON = {
  investigation: FileText,
  identity: Fingerprint,
  account: MapPin,
  finding: AlertOctagon,
};

export default function GlobalSearch() {
  const { API } = useApp();
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    if (q.length < 2) { setResults([]); return; }
    const ctl = new AbortController();
    setLoading(true);
    const t = setTimeout(() => {
      axios.get(`${API}/search`, { params: { q }, signal: ctl.signal })
        .then((r) => { setResults(r.data.results); setLoading(false); setActive(0); })
        .catch(() => setLoading(false));
    }, 180);
    return () => { clearTimeout(t); ctl.abort(); };
  }, [q, API]);

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const go = (r) => {
    setOpen(false);
    setQ('');
    navigate(r.route);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    else if (e.key === 'Enter' && results[active]) go(results[active]);
    else if (e.key === 'Escape') setOpen(false);
  };

  return (
    <div ref={ref} className="hidden lg:flex flex-1 max-w-md mx-2 relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none" />
      <input
        data-testid="header-search"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder="Search identities, resources, findings, evidence…"
        className="w-full bg-slate-800/60 border border-slate-700 text-sm text-white placeholder:text-slate-500 rounded-md py-1.5 pl-9 pr-3 focus:outline-none focus:border-apex-lens"
      />
      {loading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 animate-spin" />}

      {open && q.length >= 2 && (
        <div data-testid="search-dropdown" className="absolute top-full left-0 right-0 mt-1 bg-apex-shell border border-slate-700 rounded-md shadow-2xl overflow-hidden z-[5000] max-h-[420px] overflow-y-auto">
          {results.length === 0 && !loading && (
            <div className="px-3 py-4 text-xs text-slate-400 text-center">No results for "{q}"</div>
          )}
          {results.map((r, i) => {
            const Icon = ICON[r.kind] ?? Search;
            return (
              <button
                key={`${r.kind}-${r.id}`}
                onClick={() => go(r)}
                onMouseEnter={() => setActive(i)}
                data-testid={`search-result-${r.kind}-${r.id}`}
                className={`w-full text-left px-3 py-2.5 flex items-center gap-2.5 border-b border-slate-800 last:border-b-0 transition-colors ${i === active ? 'bg-slate-800/80' : 'hover:bg-slate-800/50'}`}
              >
                <Icon className="w-3.5 h-3.5 text-apex-lens shrink-0" />
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-semibold text-white truncate">{r.label}</div>
                  <div className="text-[10px] font-mono text-slate-400 truncate">{r.sublabel}</div>
                </div>
                <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold shrink-0">{r.kind}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
